/**
 * Read models for the audit trail, mirroring the Auditing service `AuditEntryResource`
 * (CheqMe.Server.Services.Auditing/Controller/v2025_01). The trail is append-only and read through the
 * audit-oversight roles (PlatformOwner / CompanyOwner / Auditor). Actor display names are stamped at write time;
 * the GUID fields (company/shard, and the occasional user id) are resolved to names via the monolith resolver.
 */

/** Mirrors the backend AuditOutcome enum (serialised as its numeric value). */
export enum AuditOutcome {
  Success = 0,
  Denied = 1,
  Failure = 2,
  /** Degraded but handled — the action completed under an abnormal condition that warrants attention (#F26). */
  Warning = 3
}

/** One audit entry as returned by the query API. */
export type AuditEntry = {
  id: string;
  occurredAtUtc: string;
  category: string;
  action: string;
  targetType?: string;
  targetId?: string;
  outcome: AuditOutcome;
  detail?: string;
  /** The acting principal's id (a user or employee GUID), if any. */
  actorUserId?: string;
  /** The actor's display name as stamped at write time (already human-readable when present). */
  actorDisplayName?: string;
  /** Comma-joined central roles the actor held (e.g. "ShardAdmin,Auditor"). */
  actorRole?: string;
  /** Owning company GUID (resolved to a name in the UI). */
  companyId?: string;
  /** Owning shard GUID (resolved to a name in the UI). */
  shardId?: string;
  /** How the session authenticated (e.g. "Local", "Entra"). */
  authSource?: string;
  /** Originating external identity id when the session came via social/Entra. */
  externalId?: string;
  /** The platform-owner principal id that started an impersonation, if any (resolved to a name in the UI). */
  impersonatedBy?: string;
};

/** Optional server-side filters for the audit query (all combine with AND). */
export type AuditQuery = {
  category?: string;
  targetType?: string;
  targetId?: string;
  /** ISO-8601 UTC lower bound (inclusive). */
  from?: string;
  /** ISO-8601 UTC upper bound (inclusive). */
  to?: string;
  take?: number;
};

/** The ids the UI asks the monolith to resolve to names. */
export type ResolveNamesRequest = {
  userIds?: string[];
  companyIds?: string[];
  shardIds?: string[];
};

/** id → display-name maps; ids that couldn't be resolved are simply absent. */
export type ResolvedNames = {
  users: Record<string, string>;
  companies: Record<string, string>;
  shards: Record<string, string>;
};

/** German label for an audit outcome. */
export function auditOutcomeLabel(outcome: AuditOutcome): string {
  switch (outcome) {
    case AuditOutcome.Success: return 'Erfolg';
    case AuditOutcome.Denied: return 'Verweigert';
    case AuditOutcome.Failure: return 'Fehler';
    case AuditOutcome.Warning: return 'Warnung';
    default: return '–';
  }
}

/** Tailwind chip classes for an audit outcome (matches the style guide's status-badge palette). */
export function auditOutcomeClass(outcome: AuditOutcome): string {
  switch (outcome) {
    case AuditOutcome.Success: return 'bg-green-50 text-green-700';
    case AuditOutcome.Denied: return 'bg-yellow-50 text-yellow-700';
    case AuditOutcome.Failure: return 'bg-red-50 text-red-700';
    case AuditOutcome.Warning: return 'bg-orange-50 text-orange-700';
    default: return 'bg-gray-100 text-gray-600';
  }
}
